/* ============================================================
   SetupJourneyWidget — research-first onboarding chain on the
   dashboard (port of research-pack/research-11-setup-journey).
   Steps: research (live status from the research worker) →
   strategy → first post. The research worker drives the tail of
   the chain server-side (it kicks the first-post bootstrap the
   moment an onboarding run turns terminal), so progress never
   depends on this widget being mounted. Here we only WATCH for
   the strategy + post appearing; direct generation remains the
   fallback — immediately when the kickoff never produced a
   report (old-flow behavior), or after a generous budget when
   the server kick was lost.
   Engages ONLY for a workspace whose research-first onboarding
   just happened: OnboardingActivation drops a per-user pending
   marker, and all journey keys are user-scoped so a shared
   browser never leaks one account's journey into another.
   ============================================================ */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icons.jsx';
import { api } from '../../services/api/client.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { isPostingPlan } from '../../lib/finishSetup.js';
import { researchOnboardingEnabled, JOURNEY_PENDING_KEY } from '../../screens/OnboardingActivation.jsx';
import FirstPostReadyCard from './FirstPostReadyCard.jsx';

const RESEARCH_ACTIVE = ['queued', 'gathering', 'analyzing', 'synthesizing', 'verifying'];
const POLL_MS = 15000;
// The kickoff is best-effort and D1 lags — give the report this many empty
// list polls before falling back to direct generation (old-flow behavior).
const MAX_EMPTY_POLLS = 4;
// The research worker kicks the chain tail (strategy + first post) server-side
// the moment the run turns terminal. The widget watches for the result this
// many polls before assuming the kick was lost and generating directly —
// generation itself takes 1–3 minutes, so the budget must exceed it.
const SERVER_CHAIN_MAX_POLLS = 20; // × POLL_MS ≈ 5 min
// A run that finished longer ago than this already had its server kick (it
// fires within ~3 min of completion) — don't make the user sit through the
// watch budget; go straight to the idempotent get-or-create.
const SERVER_CHAIN_STALE_MS = 10 * 60 * 1000;
const PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function unwrap(res) {
  return res?.data ?? res;
}

function readLocal(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function writeLocal(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (_) { /* private mode */ }
}

function removeLocal(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (_) { /* private mode */ }
}

// Retire the dashboard first-post journey card — but ONLY once the user has
// actually OPENED the post (FirstPostReveal calls this on a successful load).
// Until then the card persists across reloads so the user can never lose the
// entry point if a navigation bounces back before they saw the post.
export function markFirstPostJourneyVisited(userId) {
  const id = userId ?? 'anon';
  writeLocal(`dathent.research.journey.done.${id}`, '1');
  removeLocal(`${JOURNEY_PENDING_KEY}.${id}`);
}

export default function SetupJourneyWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const userId = user?.id ?? null;
  const dash = useDashboardData() || {};
  // Owner decision 2026-09-07 (item 4): this card narrates a research ->
  // strategy -> first-post pipeline posting doesn't have (Posting is
  // skipped_plan — no research/strategy/AI generation by design). The
  // JOURNEY_PENDING_KEY marker is no longer armed for posting at the
  // source (OnboardingActivation.jsx's handleContinueFromConnect), but an
  // account onboarded before that fix can still carry a stale marker in
  // its browser — defend here too. planKnown-gated so a posting account
  // never flashes this card for the one tick before entitlements resolve
  // (isPostingPlan(undefined) would otherwise read false and let it engage).
  const planKnown = Boolean(dash.fetchedAt?.entitlements);
  const posting = isPostingPlan(dash.entitlements);

  const doneKey = `dathent.research.journey.done.${userId ?? 'anon'}`;
  const postKey = `dathent.research.journey.postId.${userId ?? 'anon'}`;
  const pendingKey = `${JOURNEY_PENDING_KEY}.${userId ?? 'anon'}`;

  // Engage only for this user's fresh research-first onboarding.
  const engaged = (() => {
    if (!researchOnboardingEnabled() || !userId) return false;
    if (!planKnown || posting) return false;
    if (readLocal(doneKey) === '1') return false;
    const pendingAt = Number(readLocal(pendingKey) || 0);
    return pendingAt > 0 && Date.now() - pendingAt < PENDING_MAX_AGE_MS;
  })();

  const [dismissed, setDismissed] = useState(false);
  const [research, setResearch] = useState(null);
  const [emptyPolls, setEmptyPolls] = useState(0);
  const [hasStrategy, setHasStrategy] = useState(false);
  const [postId, setPostId] = useState(() => readLocal(postKey) || null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState(false);
  // A 402 (card gate) is NOT "research missing": before this state existed the
  // widget counted billing blocks as empty polls, concluded the pipeline died,
  // fired the client fallback (also 402) and froze at a fabricated percent —
  // while the content sat fully generated server-side (owner-hit 2026-07-11).
  const [billingBlocked, setBillingBlocked] = useState(false);
  // TASK-331: which 402 reason blocked the pipeline — used only to pick
  // "Activate" vs "Renew" copy on the same billingBlocked card below.
  const [billingBlockedReason, setBillingBlockedReason] = useState('quota'); // 'quota' | 'lapsed'
  const generationFired = useRef(false);
  const pollRef = useRef(null);

  const loadResearch = useCallback(async () => {
    try {
      const list = unwrap(await api.research.list());
      const latest = (list?.reports || [])[0] || null;
      setResearch(latest);
      if (!latest) setEmptyPolls((n) => n + 1);
    } catch (err) {
      if (err?.status === 402) {
        setBillingBlockedReason(err?.code === 'PLAN_LAPSED' ? 'lapsed' : 'quota');
        setBillingBlocked(true);
        return;
      }
      setEmptyPolls((n) => n + 1);
    }
  }, []);

  useEffect(() => {
    if (!engaged || dismissed) return;
    loadResearch();
  }, [engaged, dismissed, loadResearch]);

  // Background tabs throttle intervals hard — refresh the moment the user
  // comes back so the chain never looks stuck until a manual reload.
  useEffect(() => {
    if (!engaged || dismissed) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        api.invalidateCache?.('/api/research');
        loadResearch();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [engaged, dismissed, loadResearch]);

  // Poll while the run is active OR while we are still waiting for the
  // kicked-off report to appear (D1 lag) — up to MAX_EMPTY_POLLS.
  const researchStatus = research?.status || null;
  const waitingForReport = !research && emptyPolls > 0 && emptyPolls < MAX_EMPTY_POLLS;
  useEffect(() => {
    if (!engaged || dismissed || billingBlocked) return undefined;
    const active = (researchStatus && RESEARCH_ACTIVE.includes(researchStatus)) || waitingForReport;
    if (!active) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    if (!pollRef.current) {
      pollRef.current = setInterval(() => {
        api.invalidateCache?.('/api/research');
        loadResearch();
      }, POLL_MS);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [engaged, dismissed, billingBlocked, researchStatus, waitingForReport, loadResearch]);

  // Research terminal — the worker already kicked the chain tail server-side
  // (strategy bootstrap + first post), so WATCH for the result instead of
  // generating: poll the strategy + content list until the first post shows
  // up. Direct generation is the fallback — immediately when the report is
  // confirmed missing (kickoff failed → old-flow behavior), or after
  // SERVER_CHAIN_MAX_POLLS when the server kick was lost. forceNew:false
  // keeps the fallback idempotent: it reuses the server's post if both raced.
  const researchMissing = !research && emptyPolls >= MAX_EMPTY_POLLS;
  const researchTerminal = researchStatus === 'ready' || researchStatus === 'failed' || researchMissing;
  useEffect(() => {
    if (!engaged || dismissed || !researchTerminal || postId || generationFired.current) return undefined;
    let cancelled = false;
    let timer = null;

    const finishWithPost = (id) => {
      // Persist BEFORE the cancelled check — a found/generated post must
      // survive an unmount, or a remount refires the chain in parallel.
      writeLocal(postKey, id);
      if (cancelled) return;
      setPostId(id);
      setHasStrategy(true);
      setDrafting(false);
    };

    const generateDirect = async () => {
      generationFired.current = true;
      setDrafting(true);
      try {
        const strategyRes = await api.strategy.get().catch(() => null);
        if (!cancelled) setHasStrategy(Boolean(unwrap(strategyRes)?.strategy));
        const res = unwrap(await api.content.generateFirstPost({ includePremiumImage: true, forceNew: false }));
        const id = res?.content?.contentItem?.id || res?.content?.id || res?.firstPost?.id || res?.id || null;
        if (id) {
          finishWithPost(id);
        } else if (!cancelled) {
          setDraftError(true);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 402) {
          setBillingBlockedReason(err?.code === 'PLAN_LAPSED' ? 'lapsed' : 'quota');
          setBillingBlocked(true);
        } else setDraftError(true);
      } finally {
        if (!cancelled) setDrafting(false);
      }
    };

    // One watch round: surface the strategy as soon as it lands (step 2 turns
    // done while step 3 keeps drafting), then look for the first post.
    const watchOnce = async () => {
      try {
        api.invalidateCache?.('/api/strategy');
        const strategyRes = await api.strategy.get().catch(() => null);
        if (!cancelled && unwrap(strategyRes)?.strategy) setHasStrategy(true);
        api.invalidateCache?.('/api/content');
        const listRes = await api.content.list({ limit: 20 }).catch(() => null);
        const rows = unwrap(listRes)?.content || [];
        const found = rows.find((row) => row?.contentItem?.source === 'first_post');
        const id = found?.contentItem?.id || null;
        // The post isn't truly ready until its IMAGE finishes too — keep the
        // "first post" step working (don't collapse to the done card / reveal)
        // while the visual job is still cooking. Null/own/succeeded = ready.
        const imageCooking = found?.contentItem?.format === 'image_post'
          && ['queued', 'processing', 'generating', 'visualizing', 'pending'].includes(String(found?.jobVisualStatus || ''));
        if (id && !imageCooking) {
          finishWithPost(id);
          return true;
        }
      } catch (_) { /* keep watching */ }
      return false;
    };

    // No report (kickoff failed), a non-onboarding run (no server kick), or a
    // run that finished long ago (its kick already happened — or never will):
    // generate directly. The get-or-create reuses any existing post anyway.
    const completedAtMs = research?.completedAt ? Date.parse(research.completedAt) : NaN;
    const serverChainStale = research
      && (research.trigger !== 'onboarding'
        || (Number.isFinite(completedAtMs) && Date.now() - completedAtMs > SERVER_CHAIN_STALE_MS));
    if (researchMissing || serverChainStale) {
      // LOOK before generating: the post may already exist (server chain ran
      // long ago, or generation is billing-blocked while the content sits
      // ready). Reads survive any billing state, so one watch round finds it
      // and collapses straight to the done card instead of firing a POST that
      // can 402 into the activation card while the post is actually there.
      (async () => {
        if ((await watchOnce()) || cancelled) return;
        generateDirect();
      })();
      return () => { cancelled = true; };
    }

    setDrafting(true);
    let polls = 0;
    (async () => {
      if (await watchOnce() || cancelled) return;
      timer = setInterval(async () => {
        if (cancelled) return;
        polls += 1;
        const found = await watchOnce();
        if (cancelled || found) {
          if (timer) { clearInterval(timer); timer = null; }
          return;
        }
        if (polls >= SERVER_CHAIN_MAX_POLLS) {
          if (timer) { clearInterval(timer); timer = null; }
          generateDirect();
        }
      }, POLL_MS);
    })();
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [engaged, dismissed, researchTerminal, researchMissing, postId, postKey]);

  if (!engaged) return <FirstPostReadyCard />;
  if (dismissed) return null;

  // Billing block (server 402): a clear "activate" state instead of a frozen
  // progress card — the pipeline output is safe server-side, it's access that
  // needs restoring.
  if (billingBlocked) {
    const lapsed = billingBlockedReason === 'lapsed';
    return (
      <section className="card sj-done">
        <span className="sj-ok"><Icon name="sparkles" size={14} /></span>
        <div className="sj-tx">
          <div className="sj-tt">
            {lapsed
              ? t('lapsed.bannerTitle', { defaultValue: 'Your plan has lapsed' })
              : t('journey.billingBlockedTitle', { defaultValue: 'Your setup is ready — activate to unlock it' })}
          </div>
          <div className="sj-ts">
            {lapsed
              ? t('lapsed.bannerBody', { defaultValue: 'Everything you have created is still here to read. Creating, generating and publishing are paused until you renew.' })
              : t('journey.billingBlockedSub', { defaultValue: 'Your research, strategy and first post are safe. Activate your plan to open them.' })}
          </div>
        </div>
        <button type="button" className="btn btn-primary sj-cta" onClick={() => navigate('/app/settings?tab=billing')}>
          {lapsed ? t('lapsed.renew', { defaultValue: 'Renew your plan' }) : t('journey.billingBlockedCta', { defaultValue: 'Activate' })} <Icon name="arrowRight" size={13} />
        </button>
      </section>
    );
  }

  if (!research && !waitingForReport && !researchMissing) return null;

  const openReveal = () => {
    // Only HIDE the card for this session — do NOT retire the journey here.
    // FirstPostReveal marks it visited once the post actually loads, so the
    // entry point survives reloads until the user has really opened the post.
    setDismissed(true);
    navigate(`/app/reveal/${encodeURIComponent(postId)}`);
  };

  // Collapsed done card (state D).
  if (postId) {
    return (
      <section className="card sj-done">
        <span className="sj-ok"><Icon name="check" size={14} /></span>
        <div className="sj-tx">
          <div className="sj-tt">{t('journey.doneTitle')}</div>
          <div className="sj-ts">{t('journey.doneSub')}</div>
        </div>
        <button type="button" className="btn btn-primary sj-cta" onClick={openReveal}>
          {t('journey.doneCta')} <Icon name="arrowRight" size={13} />
        </button>
      </section>
    );
  }

  const researchDone = researchStatus === 'ready';
  const researchFailed = researchStatus === 'failed' || researchMissing;
  const steps = [
    {
      key: 'research',
      label: t('journey.stepResearch'),
      icon: 'search',
      state: researchDone ? 'done' : researchFailed ? 'failed' : 'run',
      // Clickable as soon as a research run exists (running OR done) — the
      // Research tab shows the live progress while it's still gathering, so the
      // user can click through and watch it there. No visual change to the step.
      href: research ? '/app/research' : null,
      status: researchDone
        ? t('journey.stDoneOpen')
        : researchFailed
          ? t('journey.stFailed')
          : t('journey.stRunning'),
    },
    {
      key: 'strategy',
      label: t('journey.stepStrategy'),
      icon: 'target',
      state: hasStrategy ? 'done' : (researchTerminal && drafting) ? 'run' : 'pend',
      href: hasStrategy ? '/app/plan?tab=plan' : null,
      status: hasStrategy ? t('journey.stDoneOpen') : (researchTerminal && drafting) ? t('journey.stRunning') : t('journey.stPending'),
    },
    {
      key: 'post',
      label: t('journey.stepFirstPost'),
      icon: 'sparkles',
      state: drafting && hasStrategy ? 'run' : 'pend',
      href: null,
      status: draftError
        ? t('journey.stFailed')
        : drafting && hasStrategy
          ? t('journey.stDrafting')
          : t('journey.stPending'),
    },
  ];
  const doneCount = steps.filter((s) => s.state === 'done').length;
  const stepNumber = Math.min(doneCount + 1, 3);

  // Derived hero state — there's no real % from the backend, so climb it by
  // the step the chain has reached (research → strategy → first post).
  const pct = (() => {
    const [r, s, p] = steps.map((x) => x.state);
    if (p === 'run') return 90;
    if (s === 'done') return 76;
    if (s === 'run') return 60;
    if (r === 'done') return 44;
    if (r === 'run') return 26;
    return 10;
  })();
  const currentLabel = steps.find((x) => x.state === 'run')?.label || t('journey.finishing');
  const etaMin = Math.max(1, 3 - doneCount);
  const deck = [
    { key: 'research', icon: 'search', title: t('journey.stepResearch'), real: t('journey.deckResearch'), ready: researchDone },
    { key: 'strategy', icon: 'target', title: t('journey.stepStrategy'), real: t('journey.deckStrategy'), ready: hasStrategy },
    { key: 'post', icon: 'sparkles', title: t('journey.stepFirstPost'), real: t('journey.deckPost'), ready: false },
  ];
  const readyCount = deck.filter((d) => d.ready).length;

  return (
    <section className="card sj-hero">
      <div className="sj-aura" aria-hidden="true" />
      <div className="sj-sweep" aria-hidden="true" />
      <div className="sj-hero-in">
        <div className="sj-hero-top">
          <span className="sj-orb" aria-hidden="true">
            <span className="sj-orb-core">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 13.8 8.2 19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /></svg>
            </span>
          </span>
          <div className="sj-hero-tl">
            <span className="sj-live"><i className="sj-live-d" /> {t('journey.stepOf', { n: stepNumber })}</span>
            <h2 className="sj-hero-h">{t('journey.heroPre')} <span className="sj-g">{t('journey.heroAccent')}</span></h2>
            <p className="sj-hero-sub">{t('journey.subtitle')}</p>
          </div>
        </div>

        <div className="sj-pband">
          <div className="sj-pband-meta">
            <span className="sj-pband-now">{currentLabel}</span>
            <span className="sj-pband-pct"><b>{pct}</b>%</span>
          </div>
          <div className="sj-ptrack"><div className="sj-pfill" style={{ width: `${pct}%` }} /></div>
        </div>

        <div className="sj-stages">
          {steps.map((s) => {
            const clickable = Boolean(s.href);
            return (
              <div
                key={s.key}
                className={`sj-st is-${s.state}`}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                style={clickable ? { cursor: 'pointer' } : undefined}
                onClick={clickable ? () => navigate(s.href) : undefined}
                onKeyDown={clickable ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(s.href); }
                } : undefined}
              >
                <span className="sj-st-i"><Icon name={s.state === 'done' ? 'check' : s.icon} size={13} /></span>
                <div className="sj-st-n">{s.label}</div>
                <div className="sj-st-s">{s.status}</div>
              </div>
            );
          })}
        </div>

        <div className="sj-reveal">
          <div className="sj-reveal-l">
            <span className="sj-reveal-t"><Icon name="sparkles" size={13} /> {t('journey.deckTitle')}</span>
            <span className="sj-reveal-c">{t('journey.deckCount', { ready: readyCount, total: 3 })}</span>
          </div>
          <div className="sj-deck">
            {deck.map((d) => (
              <div key={d.key} className={`sj-ds ${d.ready ? 'is-ready' : ''}`}>
                <div className="sj-ds-h">
                  <span className="sj-ds-i"><Icon name={d.icon} size={12} /></span>
                  <span className="sj-ds-t">{d.title}</span>
                  <span className="sj-ds-pop"><Icon name="check" size={10} /></span>
                </div>
                <div className="sj-sk" style={{ width: '90%' }} />
                <div className="sj-sk" style={{ width: '66%' }} />
                <div className="sj-ds-real">{d.real}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="sj-hfoot">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
          <span>{t('journey.footReassure')}</span>
          <span className="sj-hfoot-sp" />
          <span className="sj-eta">~<b>{etaMin}</b> {t('journey.etaMin')}</span>
        </div>

        {draftError && <div className="sj-back">{t('journey.draftRetryNote')}</div>}
      </div>
    </section>
  );
}

function JourneyNode({ step, linked, prevDone }) {
  const dot = (
    <span className="sj-nd-dot"><Icon name={step.state === 'done' ? 'check' : step.icon} size={14} /></span>
  );
  const node = (
    <span className={`sj-nd is-${step.state}`}>
      {dot}
      <span className="sj-nd-l">{step.label}</span>
      <span className="sj-nd-s">{step.status}</span>
    </span>
  );
  return (
    <>
      {linked && <span className={`sj-lk ${prevDone ? 'is-fill' : ''}`} aria-hidden="true"><i /></span>}
      {step.href ? <Link to={step.href} className="sj-nd-link">{node}</Link> : node}
    </>
  );
}
